import type { ConsignmentOrder } from '@/types/consignmentOrders';
import { formatDate } from '@/utils';
import { getOrderStatusLabel, getFedExShippingMethodLabel, buildShippingLocationString } from '@/components/Templates/Requests/utils/parseOrderDetails';

/**
 * Consignment orders always show "Trunk Stock" as order type
 */
const getConsignmentOrderType = (): string => {
	return 'Trunk Stock';
};

/**
 * Build array of product strings for bulk consignment orders (for tile/badge display in DetailsView)
 * Each item: "name (size) (qty)"
 */
const buildBulkConsignmentProductsArray = (orders: ConsignmentOrder[]): string[] => {
	if (!orders?.length) return [];
	return orders.map((order) => {
		const name = order.ta_SKU?.ta_name ?? '-';
		const size = order.ta_SKU?.ta_size ?? '-';
		const qty = order.ta_quantity?.toString() ?? '-';
		return `${name} (${size}) (${qty})`;
	});
};

/**
 * Parse bulk consignment order data into DetailsView format
 * Shows shared order info and a single "Products" field with all products as tiles/badges (like BV)
 */
export const parseBulkConsignmentOrderDetailsForModal = (orders: ConsignmentOrder[]) => {
	if (!orders?.length) return null;
	const order = orders[0];
	const orderType = getConsignmentOrderType();

	return [
		// Column 1: Order & Clinic Information
		{
			data: [
				{ label: 'Orders Type', value: orderType },
				{ label: 'Provider', value: order.ta_Doctor?.fullname || '-' },
				{ label: 'Clinic', value: order.ta_Clinic?.name || '-' },
				{ label: 'Status', value: getOrderStatusLabel(order.ta_status) },
				{ label: 'Request Date', value: formatDate(order.ta_orderrequestdate) },
				{ label: 'Date of Service', value: formatDate(order.ta_dos) },
				{ label: 'Clinic PO', value: order.ta_clinicpo || '-' },
			],
		},
		// Column 2: Products (tiles/badges like BV) & Shipping Information
		{
			data: [
				{ label: 'Products', value: buildBulkConsignmentProductsArray(orders) },
				{ label: 'Shipping Location', value: buildShippingLocationString(order) },
				{ label: 'Shipping Method', value: getFedExShippingMethodLabel((order as any).ta_fedexshippingmethod) },
				{ label: 'Comments', value: (order as any).ta_comments || '-' },
			],
		},
	];
};

/**
 * Parse consignment order data into DetailsView format
 * Transforms raw order data into 2-column structured display
 * Shows consignment type (Trunk Stock or Consignment) instead of generic "Bulk"
 */
export const parseConsignmentOrderDetailsForModal = (order: ConsignmentOrder) => {
	const orderType = getConsignmentOrderType();

	return [
		// Column 1: Order & Clinic Information
		{
			data: [
				{ label: 'Orders Type', value: orderType },
				{ label: 'Provider', value: order.ta_Doctor?.fullname || '-' },
				{ label: 'Clinic', value: order.ta_Clinic?.name || '-' },
				{ label: 'Status', value: getOrderStatusLabel(order.ta_status) },
				{ label: 'Request Date', value: formatDate(order.ta_orderrequestdate) },
				{ label: 'Date of Service', value: formatDate(order.ta_dos) },
				{ label: 'Clinic PO', value: order.ta_clinicpo || '-' },
			],
		},
		// Column 2: Product & Shipping Information
		{
			data: [
				{ label: 'Product', value: order.ta_SKU?.ta_name || '-' },
				{ label: 'Quantity', value: order.ta_quantity?.toString() || '-' },
				{ label: 'Size', value: order.ta_SKU?.ta_size || '-' },
				{ label: 'Shipping Location', value: buildShippingLocationString(order) },
				{ label: 'Shipping Method', value: getFedExShippingMethodLabel((order as any).ta_fedexshippingmethod) },
				{ label: 'Comments', value: (order as any).ta_comments || '-' },
			],
		},
	];
};

